<script setup lang="ts">
import { saveAs } from 'file-saver'
import { Icon } from '@wartek-id/belajar-id-components'
import { useRouter } from 'vue-router'
import { Button } from '@/components'
import { useAccountLookup, useGA } from '@/composables'
import { useAuthModal } from '@/composables/useAuthModal'
import ModalIframe from '@/components/ModalIframe/ModalIframe.vue'
import { trackSatuEvent } from '@/utilities/satuEvent'

const store = useStore()
const router = useRouter()

const { pageView, trackActivity } = useGA()
const { setForm } = useAccountLookup()
const { type, show: showModal } = useAuthModal()

const files = [
  {
    title: 'Unduh Dokumen Resmi',
    name: 'PERSESJEN NOMOR 4 TAHUN 2024_AKUN AKSES',
    href: '/static/SALINAN_PERSESJEN NOMOR 4 TAHUN 2024_AKUN AKSES.pdf',
  },
]

const isWebinarModalOpen = ref(false)
const webinarTitle = 'Webinar'
const webinarUrl = 'https://docs.google.com/spreadsheets/d/e/2PACX-1vT8-sR2r25iz_tVJQk9v89DKoqm8uB7QWdtn7s50YOeoSk23HxXvhabwpMov2J6gmF85lTHmNLsnG16/pubhtml?widget=true&amp;headers=false'

const partners = ref([
  {
    name: 'Rumah Pendidikan',
    href: 'https://rumah.pendidikan.go.id/',
    src: '/images/new-homepage/logo-rumdik.png',
    alt: 'Logo Rumah Pendidikan',
    imgClass: 'h-[54px] mb-[6px]',
    title: 'Ruang Kolaborasi untuk Pendidikan Bermutu',
  },
  {
    name: 'Assemblr Edu',
    href: 'https://edu.assemblrworld.com/',
    src: '/images/new-homepage/logo-assemblr-edu.png',
    alt: 'Logo Assemblr Edu',
    imgClass: 'h-[50px] mb-[10px]',
    title: 'Eksplorasi dunia 3D & AR di Assemblr EDU dengan belajar.id',
  },
  {
    name: 'Canva',
    href: 'https://www.canva.com/',
    src: '/images/new-homepage/logo-canva.png',
    alt: 'Logo Canva',
    imgClass: 'h-[60px]',
    title: 'Desain kreatif tanpa batas untuk belajar mengajar dengan Canva Pendidikan',
  },
  {
    name: 'Google Workspace for Education',
    href: 'https://edu.google.com/',
    src: '/images/new-homepage/logo-google-edu.png',
    alt: 'Logo Google Edu',
    imgClass: 'h-[55px] mb-[5px]',
    title: 'Google sebagai perangkat pendukung belajar mengajar',
  },
])

const scrollToUser = () => {
  const userSection = document.querySelector('#user-section') as HTMLElement

  if (userSection)
    userSection.scrollIntoView({ behavior: 'smooth' })
}

const scrollToForgotPassword = () => {
  const forgotPassword = document.querySelector('#forgot-password-section') as HTMLElement

  if (forgotPassword) {
    const yOffset = -120 // Offset for sticky header
    const y = forgotPassword.getBoundingClientRect().top + window.pageYOffset + yOffset
    window.scrollTo({ top: y, behavior: 'smooth' })
  }
}

const scrollToAjukanPesertaDidik = () => {
  const ajukanSection = document.querySelector('#ajukan-akun-ptk') as HTMLElement

  if (ajukanSection) {
    const yOffset = -120 // Offset for sticky header
    const y = ajukanSection.getBoundingClientRect().top + window.pageYOffset + yOffset
    window.scrollTo({ top: y, behavior: 'smooth' })
  }
}

const showModalHandler = (typeVal: string) => {
  if (typeVal === 'pengajuan-akun') {
    type.value = typeVal
    showModal.value = true
  }
  else {
    window.location.href = import.meta.env.VITE_ADMIN_BASE_URL
  }
}

const isOpenModalResetPassword = computed({
  get() {
    return store.state.resetPassword.isOpen
  },
  set(val) {
    store.commit('resetPassword/setModal', val)
  },
})

const openModalResetPassword = () => {
  store.commit('resetPassword/openModal')
  trackSatuEvent({
    event_name: 'PASSWORD_RESET_INITIATED',
    page_title: 'Home Page',
  })
}

const trackAccountType = (type: string) => {
  const params: Record<string, Record<string, any>> = {
    ptk: {
      activity_type: 'CHOOSE_ACCOUNT_TYPE',
      account_type: 'Pendidik dan Tenaga Kependidikan',
      page_title: 'Belajar.id | Home Page',
    },
    pd: {
      activity_type: 'CHOOSE_ACCOUNT_TYPE',
      account_type: 'Peserta Didik',
      page_title: 'Belajar.id | Home Page',
    },
    dinas: {
      activity_type: 'CHOOSE_ACCOUNT_TYPE',
      account_type: 'Dinas',
      page_title: 'Belajar.id | Home Page',
    },
  }

  if (!params[type])
    return

  trackActivity(params[type])
}

const onChoose = (type: string) => {
  store.commit('accountLookup/setUserType', type)
  setForm({ userType: type })

  if (type === 'dinas')
    router.push('/account/dinas')
  else
    router.push('/account/lookup/v2')
}

const trackPartnerClick = (partnerName: string, partnerUrl: string) => {
  trackSatuEvent({
    event_name: 'CLICKED',
    page_title: 'Home Page',
    attributes: [
      {
        key: 'element_text',
        value: partnerName,
      },
      {
        key: 'destination_url',
        value: partnerUrl,
      },
      {
        key: 'page_location',
        value: 'body',
      },
    ],
  })
}

const trackWebinarClick = () => {
  trackSatuEvent({
    event_name: 'CLICKED',
    page_title: 'Home Page',
    attributes: [
      {
        key: 'element_text',
        value: 'Tonton Webinar',
      },
      {
        key: 'page_location',
        value: 'body',
      },
    ],
  })
}

const trackAjukanAkunDinas = () => {
  trackSatuEvent({
    event_name: 'DINAS_ACCOUNT_REQUEST_INITIATED',
    page_title: 'Home Page',
  })
  router.push('/account/dinas')
}

const trackAjukanAkunPesertaDidik = () => {
  trackSatuEvent({
    event_name: 'PD_ACCOUNT_REQUEST_INITIATED',
    page_title: 'Home Page',
  })
  showModalHandler('pengajuan-akun')
}

const trackKunjungiPusatBantuan = () => {
  trackSatuEvent({
    event_name: 'CLICKED',
    page_title: 'Home Page',
    attributes: [
      {
        key: 'element_text',
        value: 'Kunjungi Pusat Bantuan',
      },
      {
        key: 'destination_url',
        value: 'https://pusatinformasi.rumahpendidikan.kemendikdasmen.go.id/hc/id/categories/52397558782361-Akun-belajar-id',
      },
      {
        key: 'page_location',
        value: 'body',
      },
    ],
  })
  window.open('https://pusatinformasi.rumahpendidikan.kemendikdasmen.go.id/hc/id/categories/52397558782361-Akun-belajar-id', '_blank')
}

const trackGuideLink = (elementText: string, destinationUrl: string) => {
  trackSatuEvent({
    event_name: 'CLICKED',
    page_title: 'Home Page',
    attributes: [
      {
        key: 'element_text',
        value: elementText,
      },
      {
        key: 'destination_url',
        value: destinationUrl,
      },
      {
        key: 'page_location',
        value: 'body',
      },
    ],
  })
  window.open(destinationUrl, '_blank')
}

function downloadFile(path: string, name: string) {
  const filename = path.split('/').pop()
  saveAs(path, filename)
  trackSatuEvent({
    event_name: 'FILE_DOWNLOADED',
    page_title: 'Home Page',
    attributes: [
      {
        key: 'file_name',
        value: name,
      },
      {
        key: 'file_format',
        value: filename?.split('.').pop(),
      },
    ],
  })
}

const onSubmit = (type: string) => {
  switch (type) {
    case 'pd':
      pageView('PD lookup account', '/account/lookup/v2', {
        user_type: type,
      })
      trackSatuEvent({
        event_name: 'ACCOUNT_LOOKUP_INITIATED',
        page_title: 'Home Page',
        attributes: [
          {
            key: 'user_type',
            value: 'Peserta Didik',
          },
        ],
      })
      break
    case 'ptk':
      pageView('PTK lookup account', '/account/lookup/v2', {
        user_type: type,
      })
      trackSatuEvent({
        event_name: 'ACCOUNT_LOOKUP_INITIATED',
        page_title: 'Home Page',
        attributes: [
          {
            key: 'user_type',
            value: 'Pendidik dan Tenaga Kependidikan',
          },
        ],
      })
      break
    case 'dinas':
      pageView('Dinas lookup account', '/account/lookup/v2', {
        user_type: type,
      })
      trackSatuEvent({
        event_name: 'ACCOUNT_LOOKUP_INITIATED',
        page_title: 'Home Page',
        attributes: [
          {
            key: 'user_type',
            value: 'Dinas',
          },
        ],
      })
      break
  }
  trackAccountType(type)
  onChoose(type)
}

onMounted(() => {
  trackSatuEvent({
    event_name: 'PAGE_VIEWED',
    page_title: 'Home Page',
  })
})

defineExpose({
  onSubmit,
})
</script>

<template>
  <section id="hero-banner" class="bg-[#003087]">
    <div class="hero-banner container-xxl m-auto">
      <div class="banner-content z-1 mr-[50%] ml-[2rem] lg:ml-[4.5rem] pr-[6rem] block hidden md:block">
        <h1 class="text-white font-semibold text-4xl lg:text-[48px] lg:leading-[56px] mb-10 md:mb-10">
          Satu Akun Akses Layanan Pendidikan untuk beragam layanan belajar mengajar
        </h1>
        <Button
          large
          variant="secondary"
          class="!border-gray-50 w-auto mb-4 md:mb-5"
          data-testid="hero-ajukan-pd-button"
          @click="scrollToAjukanPesertaDidik"
        >
          Ajukan Akun Peserta Didik
        </Button>
        <div class="flex items-center gap-4">
          <Button
            variant="quiet"
            class="!p-0 !h-auto !text-white underline hover:!text-white !font-medium"
            data-testid="hero-scroll-button"
            @click="scrollToUser"
          >
            Cek Akun belajar.id Anda
          </Button>
          <span class="text-white opacity-40">&middot;</span>
          <Button
            variant="quiet"
            class="!p-0 !h-auto !text-white underline hover:!text-white !font-medium"
            data-testid="hero-scroll-button"
            @click="scrollToForgotPassword"
          >
            Lupa Kata Sandi
          </Button>
        </div>
      </div>
    </div>
    <div class="bg-[#003087] block md:hidden">
      <div class="px-8 pb-8 text-center">
        <h1 class="text-white font-semibold text-2xl sm:text-3xl mb-8">
          Satu Akun Akses Layanan Pendidikan untuk beragam layanan belajar mengajar
        </h1>
        <Button
          large
          variant="secondary"
          class="!border-gray-50 w-auto mb-4"
          data-testid="hero-ajukan-pd-button"
          @click="scrollToAjukanPesertaDidik"
        >
          Ajukan Akun Peserta Didik
        </Button>
        <div class="flex flex-col items-center gap-2">
          <Button
            variant="quiet"
            class="!p-0 !h-auto !text-white underline hover:!text-white !font-medium"
            data-testid="hero-scroll-button"
            @click="scrollToUser"
          >
            Cek Akun belajar.id Anda
          </Button>
          <Button
            variant="quiet"
            class="!p-0 !h-auto !text-white underline hover:!text-white !font-medium"
            data-testid="hero-scroll-button"
            @click="scrollToForgotPassword"
          >
            Lupa Kata Sandi
          </Button>
        </div>
      </div>
    </div>
  </section>

  <section id="about-section" class="bg-white px-6 md:px-10 py-12">
    <div class="container-large mx-auto px-0 md:px-10">
      <div class="mb-10">
        <h2 class="font-medium text-2xl md:text-[40px] md:leading-[50px] mb-8">
          Mengenal Akun Akses Layanan Pendidikan
        </h2>
        <p class="text-gray-500 text-xl font-light">
          Akun belajar.id merupakan Akun Akses Layanan Pendidikan yang resmi diterbitkan oleh Kementerian Pendidikan Dasar dan Menengah. Akses ini hadir untuk mewujudkan pengalaman belajar mengajar digital yang komprehensif dan terintegrasi.
        </p>
      </div>
      <div class="">
        <template v-for="(file, i) in files" :key="i">
          <div>
            <Button
              class="flex items-center !p-0 !h-auto !text-lg !text-blue-600 !font-medium"
              variant="quiet"
              :title="file.title"
              :data-testid="`download-button-${i}`"
              @click="downloadFile(file.href, file.name)"
            >
              <span class="whitespace-nowrap truncate flex-grow">
                {{ file.title }}
              </span>
              <div class="flex-shrink-0">
                <Icon :width="18" :height="18" name="download" icon="material-symbols:download" class="inline text-primary" />
              </div>
            </Button>
          </div>
        </template>
      </div>
    </div>
  </section>

  <section id="services-section" class="px-5 py-12 lg:py-16">
    <div class="container-large mx-auto">
      <h2 class="text-center text-2xl md:text-[40px] md:leading-[50px] font-medium mb-12 px-6 md:px-0">
        Manfaatkan Akses Layanan Pendidikan
      </h2>
      <div class="flex flex-wrap justify-center gap-8 lg:gap-16">
        <div
          v-for="partner in partners"
          :key="partner.src"
          class="bg-white flex flex-col items-start justify-start rounded-xl shadow h-[240px] p-4 flex-1 w-full min-w-full sm:min-w-[95%] sm:max-w-[100%] md:min-w-[45%] md:max-w-[30%] lg:min-w-[30%] lg:max-w-[30%]"
        >
          <a
            v-if="partner.href"
            :href="partner.href"
            target="_blank"
            rel="noopener noreferrer"
            @click="trackPartnerClick(partner.name, partner.href)"
          >
            <img :class="partner.imgClass" :src="partner.src" :alt="partner.alt">
          </a>
          <img
            v-else
            :class="partner.imgClass"
            :src="partner.src"
            :alt="partner.alt"
          >
          <p class="text-xl mt-6 font-light">
            {{ partner.title }}
          </p>
          <Button
            v-if="partner.href"
            class="flex items-center mt-auto self-end"
            variant="quiet"
            :href="partner.href"
            target="_blank"
            @click="trackPartnerClick(partner.name, partner.href)"
          >
            <Icon :width="24" :height="24" name="go-to-site" icon="mdi:arrow-right" class="inline text-[#054BCC]" />
          </Button>
        </div>
      </div>
    </div>
  </section>

  <section id="user-section" class="bg-expressive-dark-blue pt-12 pb-14">
    <div class="container-xl m-auto">
      <h2 class="text-center text-2xl md:text-[40px] md:leading-[50px] font-medium mb-12 text-white px-6 md:px-0">
        Siapa pengguna Akun Akses Layanan Pendidikan?
      </h2>
      <div class="flex flex-col lg:flex-row gap-6 px-6 xl:px-0">
        <div class="w-full lg:w-1/3 bg-white rounded-lg hp-user-block">
          <img
            class="w-full h-auto rounded-lg rounded-b-none"
            src="/images/new-homepage/img-foto-dinas.png"
            alt="Image Dinas"
          >
          <div class="px-6 flex flex-col gap-4 h-auto lg:h-[300px]">
            <h3 class="text-2xl font-medium mt-6 mb-4">
              Dinas
            </h3>
            <p class="text-md mb-4 text-gray-500 font-light">
              Pemerintah daerah dan instansi yang terdiri dari kepala dinas, pengawas, penilik, pamong belajar, dan staf dinas yang terdaftar dalam SIM‑Tendik.
            </p>
            <Button
              large
              variant="primary"
              class="!text-md !font-medium w-full mt-4 md:mt-auto mb-8"
              data-testid="cek-akun-dinas-button"
              @click="trackGuideLink('Cek Status Akun Dinas', 'https://pusatinformasi.rumahpendidikan.kemendikdasmen.go.id/hc/id/articles/52476157763737-Tentang-Akun-belajar-id-Untuk-Dinas-Pendidikan')"
            >
              Cek Status Akun Dinas
            </Button>
          </div>
        </div>
        <div class="w-full lg:w-1/3 bg-white rounded-lg hp-user-block">
          <img
            class="w-full h-auto rounded-lg rounded-b-none"
            src="/images/new-homepage/img-foto-guru.png"
            alt="Image Guru"
          >
          <div class="px-6 flex flex-col gap-4 h-auto lg:h-[300px]">
            <h3 class="text-2xl font-medium mt-6 mb-4">
              Pendidik dan Tenaga Kependidikan
            </h3>
            <p class="text-md mb-4 text-gray-500 font-light">
              Pendidik & Tenaga Kependidikan (PTK) yang terdiri dari guru, kepala sekolah, operator sekolah, dan tenaga kependidikan lainnya.
            </p>
            <Button
              large
              variant="primary"
              class="!text-md !font-medium w-full mt-4 md:mt-auto mb-8"
              data-testid="cek-akun-ptk-button"
              @click="onSubmit('ptk')"
            >
              Cek Status Akun PTK
            </Button>
          </div>
        </div>
        <div class="w-full lg:w-1/3 bg-white rounded-lg hp-user-block">
          <img
            class="w-full h-auto rounded-lg rounded-b-none"
            src="/images/new-homepage/img-foto-murid.png"
            alt="Image Murid"
          >
          <div class="px-6 flex flex-col gap-4 h-auto lg:h-[300px]">
            <h3 class="text-2xl font-medium mt-6 mb-4">
              Peserta Didik
            </h3>
            <p class="text-md mb-8 lg:mb-2 text-gray-500 font-light">
              Peserta Didik yang tersebar dari jenjang PAUD, SD (Paket A), SMP (Paket B), SMA (Paket C), SMK, dan SLB.
            </p>
            <Button
              large
              variant="primary"
              class="!text-md !font-medium w-full mt-4 md:mt-auto mb-8"
              data-testid="cek-akun-pd-button"
              @click="onSubmit('pd')"
            >
              Cek Status Akun Peserta Didik
            </Button>
          </div>
        </div>
      </div>
    </div>
  </section>

  <section id="how-to-get-section" class="pt-10 pb-16 bg-white">
    <div class="container mx-auto py-4 px-6 xl:px-0">
      <h2 class="text-2xl text-center lg:text-left md:text-[40px] md:leading-[50px] font-medium mb-8 lg:mb-16">
        Cara mendapatkan Akun Akses Layanan Pendidikan
      </h2>
      <div class="flex flex-col lg:flex-row justify-between items-start gap-6 mb-12 pb-6 border-b border-[#ADADAD]">
        <div class="w-full block lg:hidden">
          <img src="/images/new-homepage/img-ilustrasi-dinas-v2.png" alt="Image Ilustrasi Dinas" class="w-full">
        </div>
        <div>
          <h3 class="text-xl lg:text-2xl font-medium mb-4 md:mb-6">
            Dinas
          </h3>
          <div class="text-md lg:text-lg text-gray-500 font-light mb-4 lg:mb-8">
            Pengajuan Akun pada lingkup Dinas Pendidikan terdiri dari 3 kategori:
            <ol class="list-decimal ml-5 mt-2">
              <li>Pengajuan akun Pengawas dilakukan secara periodik melalui SIMTENDIK</li>
              <li>Pengajuan akun Penilik dilakukan secara periodik melalui SIMPENIK</li>
              <li>Pengajuan akun selain Pengawas dan Penilik diajukan melalui kanal Helpdesk Belajar.id</li>
            </ol>
          </div>
          <div class="mb-2 lg:mb-4">
            <Button
              class="flex items-center !p-0 !text-black !h-auto"
              variant="quiet"
              data-testid="btn-panduan-dinas"
              @click="trackGuideLink('Panduan Pengajuan Akun Dinas Pendidikan', 'https://pusatinformasi.rumahpendidikan.kemendikdasmen.go.id/hc/id/articles/52508819618201-Panduan-Mendapatkan-Akun-belajar-id-untuk-Dinas-Pendidikan')"
            >
              <span class="text-md lg:text-lg text-blue-600 whitespace-nowrap truncate flex-grow font-medium">
                Panduan Pengajuan Akun Dinas Pendidikan
              </span>
              <div class="flex-shrink-0">
                <Icon :width="18" :height="18" name="go-to-site" icon="ic:baseline-launch" class="inline text-blue-600" />
              </div>
            </Button>
          </div>
        </div>
        <div class="min-w-[520px] w-[520px] hidden lg:block">
          <img src="/images/new-homepage/img-ilustrasi-dinas-v2.png" alt="Image Ilustrasi Dinas" class="w-full">
        </div>
      </div>
      <div class="flex flex-col lg:flex-row justify-between items-start gap-6 mb-12 pb-6 border-b border-[#ADADAD]">
        <div class="w-full block lg:hidden">
          <img src="/images/new-homepage/img-ilustrasi-guru.png" alt="Image Ilustrasi kerjasama" class="w-full">
        </div>
        <div id="ajukan-akun-ptk">
          <h3 class="text-xl lg:text-2xl font-medium mb-4 md:mb-6">
            Pendidik dan Tenaga Kependidikan
          </h3>
          <p class="text-md lg:text-lg text-gray-500 font-light mb-4 lg:mb-8">
            Sebagai PTK, Anda akan mendapatkan Akun belajar.id jika terdaftar di Dapodik. Pengajuan akun peserta didik, hanya dapat dilakukan oleh kepala sekolah atau operator sekolah.
          </p>
          <Button
            large
            variant="primary"
            class="!font-medium !px-6 mb-4 lg:mb-8"
            data-testid="ajukan-akun-ptk-button"
            @click="trackAjukanAkunPesertaDidik"
          >
            Ajukan Akun Peserta Didik
          </Button>
          <div>
            <Button
              class="flex items-center !p-0 !text-black !h-auto mb-3"
              variant="quiet"
              data-testid="btn-panduan-pendidik"
              @click="trackGuideLink('Kunjungi Panduan Pendidik', 'https://pusatinformasi.rumahpendidikan.kemendikdasmen.go.id/hc/id/articles/52508516667033-Panduan-Mendapatkan-Akun-belajar-id-untuk-Pendidik')"
            >
              <span class="text-md lg:text-lg text-blue-600 whitespace-nowrap truncate flex-grow font-medium">
                Kunjungi Panduan Pendidik
              </span>
              <div class="flex-shrink-0">
                <Icon :width="18" :height="18" name="go-to-site" icon="ic:baseline-launch" class="inline text-blue-600" />
              </div>
            </Button>
          </div>
          <Button
            class="flex items-center !p-0 !text-black !h-auto mb-2 lg:mb-4"
            variant="quiet"
            data-testid="btn-panduan-tk"
            @click="trackGuideLink('Kunjungi Panduan Tenaga Kependidikan', 'https://pusatinformasi.rumahpendidikan.kemendikdasmen.go.id/hc/id/articles/52508621595801-Panduan-Mendapatkan-Akun-belajar-id-untuk-Admin-Sekolah')"
          >
            <span class="text-sm sm:text-md md:text-lg text-blue-600 whitespace-nowrap truncate flex-grow font-medium">
              Kunjungi Panduan Tenaga Kependidikan
            </span>
            <div class="flex-shrink-0">
              <Icon :width="18" :height="18" name="go-to-site" icon="ic:baseline-launch" class="inline text-blue-600" />
            </div>
          </Button>
        </div>
        <div class="min-w-[520px] w-[520px] hidden lg:block">
          <img src="/images/new-homepage/img-ilustrasi-guru.png" alt="Image Ilustrasi kerjasama" class="w-full">
        </div>
      </div>
      <div class="flex flex-col lg:flex-row justify-between items-start gap-6">
        <div class="w-full block lg:hidden">
          <img src="/images/new-homepage/img-ilustrasi-murid.png" alt="Image Ilustrasi Murid" class="w-full">
        </div>
        <div>
          <h3 class="text-xl lg:text-2xl font-medium mb-4 md:mb-6">
            Peserta Didik
          </h3>
          <p class="text-md lg:text-lg text-gray-500 font-light mb-4 lg:mb-8">
            Sebagai Peserta Didik, Anda akan mendapatkan Akun belajar.id melalui kepala sekolah atau operator sekolah Anda.
          </p>
          <Button
            class="flex items-center !p-0 !text-black !h-auto"
            variant="quiet"
            data-testid="btn-panduan-pd"
            @click="trackGuideLink('Kunjungi Panduan Peserta Didik', 'https://pusatinformasi.rumahpendidikan.kemendikdasmen.go.id/hc/id/articles/52508939776793-Panduan-Pengajuan-Pembuatan-Akun-belajar-id-Peserta-Didik-Secara-Bulk')"
          >
            <span class="text-md lg:text-lg text-blue-600 whitespace-nowrap truncate flex-grow font-medium">
              Kunjungi Panduan Peserta Didik
            </span>
            <div class="flex-shrink-0">
              <Icon :width="18" :height="18" name="go-to-site" icon="ic:baseline-launch" class="inline text-blue-600" />
            </div>
          </Button>
        </div>
        <div class="min-w-[520px] w-[520px] hidden lg:block">
          <img src="/images/new-homepage/img-ilustrasi-murid.png" alt="Image Ilustrasi Murid" class="w-full">
        </div>
      </div>
    </div>
  </section>

  <section id="webinar-section" class="bg-white">
    <div>
      <img
        class="w-full h-auto max-h-[400px] object-cover xl:object-top"
        src="/images/new-homepage/img-ilustrasi-webinar.png"
        alt="Logo Kemdikbudristek RI desktop"
      >
    </div>
    <div class="container m-auto px-6 lg:px-10 pt-8 pb-8 lg:pb-14">
      <div class="text-center w-full lg:w-[420px] m-auto">
        <h2 class="text-3xl font-medium mb-6">
          Webinar
        </h2>
        <p class="text-md lg:text-xl text-gray-500 font-light mb-4 lg:mb-8">
          Pilih rekaman webinar dengan topik seputar layanan pendidikan yang dapat Anda simak.
        </p>
        <Button
          large
          variant="primary"
          class=""
          data-testid="webinar-button"
          @click="() => { trackWebinarClick(); isWebinarModalOpen = true }"
        >
          Tonton Webinar
        </Button>
      </div>
    </div>
  </section>

  <section id="forgot-password-section" class="bg-white pb-10 px-6 lg:px-0">
    <div class="container-large m-auto bg-azure rounded rounded-lg">
      <div class="flex flex-col lg:flex-row justify-between flex-start lg:items-center gap-6 lg:gap-16 px-6 lg:px-10 py-6 lg:py-8">
        <div class="flex flex-col lg:flex-row gap-6 lg:gap-12 justify-between flex-start lg:items-center">
          <div class="min-w-[65px] w-[65px] lg:min-w-[100px] lg:w-[100px]">
            <img
              class="w-full h-auto"
              src="/images/new-homepage/img-ilustrasi-lock.png"
              alt="Logo Kemdikbudristek RI desktop"
            >
          </div>
          <h2 class="text-xl lg:text-3xl text-white font-medium">
            Lupa kata sandi Akun Akses Layanan Pendidikan Anda?
          </h2>
        </div>
        <div>
          <Button
            large
            variant="secondary"
            class="lg:!px-12"
            data-testid="reset-password-button"
            @click="openModalResetPassword"
          >
            Reset Password
          </Button>
        </div>
      </div>
    </div>
  </section>

  <section id="help-section" class="container-large mx-auto">
    <div class="flex flex-col lg:flex-row justify-between items-center gap-6 lg:gap-20 px-6 lg:px-20 pt-8">
      <div>
        <h2 class="text-2xl lg:text-3xl font-medium mb-8">
          Masih memiliki pertanyaan terkait Akun Akses Layanan Pendidikan?
        </h2>
        <Button
          large
          variant="primary"
          class=""
          @click="trackKunjungiPusatBantuan"
        >
          Kunjungi Pusat Bantuan
        </Button>
      </div>
      <div class="w-full md:w-[400px] md:min-w-[400px]">
        <img
          class="w-full h-auto"
          src="/images/new-homepage/img-ilustrasi-bantuan.png"
          alt="Logo Kemdikbudristek RI desktop"
        >
      </div>
    </div>
  </section>

  <!-- WEBINAR MODAL & RESET PASSWORD MODAL -->
  <div>
    <ResetPasswordLogin v-if="isOpenModalResetPassword" v-model="isOpenModalResetPassword" />
    <ModalIframe
      v-model="isWebinarModalOpen"
      :title="webinarTitle"
      :iframe-url="webinarUrl"
    />
  </div>
</template>

<route lang="yaml">
name: home
meta:
  layout: new-design2025
</route>
